import { ImageLoader } from '../../../ImageLoader';
import { useLocale } from '../../../../i18n/locale';
import { ChapterHeadline } from '../chapterComponents/ChapterHeadline';
import { ChapterText } from '../chapterComponents/ChapterText';
import {
  CustomFormattedMessage,
  TranslationsKeys,
  useCustomIntl,
} from '../../../../i18n/i18n';
import useDarkMode from '@rio-cloud/rio-uikit/useDarkMode';
import { getManualImageAsset, imageBaseNames } from './imageAssets';

type DashboardFunctionsSection = {
  text: TranslationsKeys;
  image: string;
  imageAlt: TranslationsKeys;
};

export const DashboardDashboardFunctionsChapter: React.FC = () => {
  const { locale } = useLocale();
  const { formatMessage } = useCustomIntl();
  const isDarkMode = useDarkMode();

  const headlineImage = getManualImageAsset(
    imageBaseNames.dashboard_dashboardFunctions_dashboard,
    locale,
    isDarkMode,
  );

  const sections: DashboardFunctionsSection[] = [
    {
      text: 'userManual.chapter.dashboard.dashboardFunctions.vehiclesWithTransactions',
      image: getManualImageAsset(
        imageBaseNames.dashboard_dashboardFunctions_vehiclesWithTransactions,
        locale,
        isDarkMode,
      ),
      imageAlt: 'dashboard.panels.vehicleInformation.header',
    },
    {
      text: 'userManual.chapter.dashboard.dashboardFunctions.totalCostSummary',
      image: getManualImageAsset(
        imageBaseNames.dashboard_dashboardFunctions_totalCostSummary,
        locale,
        isDarkMode,
      ),
      imageAlt: 'dashboard.panels.totalCost.header',
    },
    {
      text: 'userManual.chapter.dashboard.dashboardFunctions.costsPerService',
      image: getManualImageAsset(
        imageBaseNames.dashboard_dashboardFunctions_costsPerService,
        locale,
        isDarkMode,
      ),
      imageAlt: 'dashboard.panels.operationalCosts.header',
    },
    {
      text: 'userManual.chapter.dashboard.dashboardFunctions.vehiclesWithLowestCosts',
      image: getManualImageAsset(
        imageBaseNames.dashboard_dashboardFunctions_vehiclesWithLowestCosts,
        locale,
        isDarkMode,
      ),
      imageAlt: 'dashboard.panels.top5costs.lowest.header',
    },
    {
      text: 'userManual.chapter.dashboard.dashboardFunctions.vehiclesWithHighestCosts',
      image: getManualImageAsset(
        imageBaseNames.dashboard_dashboardFunctions_vehiclesWithHighestCosts,
        locale,
        isDarkMode,
      ),
      imageAlt: 'dashboard.panels.top5costs.highest.header',
    },
    {
      text: 'userManual.chapter.dashboard.dashboardFunctions.numberOfTransactions',
      image: getManualImageAsset(
        imageBaseNames.dashboard_dashboardFunctions_numberOfTransactions,
        locale,
        isDarkMode,
      ),
      imageAlt: 'dashboard.panels.TransactionCountPerService.header',
    },
    {
      text: 'userManual.chapter.dashboard.dashboardFunctions.costsOverTime',
      image: getManualImageAsset(
        imageBaseNames.dashboard_dashboardFunctions_costsOverTime,
        locale,
        isDarkMode,
      ),
      imageAlt: 'dashboard.panels.costsPerServiceOverTime.header',
    },
  ];

  return (
    <div>
      <div className={'row col-xs-12'}>
        <ChapterHeadline
          messageId={'userManual.chapter.dashboard.dashboardFunctions.headline'}
        />
        <div
          className={
            'display-flex col-xs-12 gap-25 flex-column flex-row-md padding-0'
          }
        >
          <ChapterText
            colSize={12}
            colSizeMd={6}
            messageId={'userManual.chapter.dashboard.dashboardFunctions.text'}
          />
          <div className="col-xs-12 col-md-6 margin-bottom-25">
            <ImageLoader
              src={headlineImage}
              className={'border'}
              alt={formatMessage({
                id: 'userManual.chapter.dashboard.dashboardFunctions.headline',
              })}
              enlargeable
            />
          </div>
        </div>
      </div>
      <div className={'row margin-top-20'}>
        <div className={'col-xs-12 margin-bottom-10'}>
          <CustomFormattedMessage id="userManual.chapter.dashboard.dashboardFunctions.functionsTitle" />
        </div>
        {sections.map((section, index) => (
          <div
            key={index}
            className={
              'col-xs-12 display-flex margin-top-10 margin-bottom-20 flex-column gap-25 flex-row-ls'
            }
          >
            <div className={'col-xs-12 col-ls-7 col-md-6'}>
              <li className="margin-left-15">
                <CustomFormattedMessage id={section.text} />
              </li>
            </div>
            <div className={'col-xs-12 col-ls-5 col-md-6'}>
              <ImageLoader
                className="max-width-100pct-xs max-width-60pct-md border"
                src={section.image}
                alt={formatMessage({ id: section.imageAlt })}
                enlargeable
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
